import { useState, useContext, useCallback, useEffect } from "react";
import { TemplateContext } from "../../../../../../contexts/TemplateContext";
import { AppContext } from "../../../../../../contexts/AppContext";
import { AuthContext } from "../../../../../../contexts/AuthContext";
import styles from "./docxTemplateWidget.module.css";
import { Template, CustomField } from "../../../../../../sdkStructs";
import { apiGetDocxTemplate, apiUploadDocxTemplate } from "../../../../../../sdk";
import TextFieldSection from "./components/TextFieldSection/TextFieldSection";
import TemplateFileSection from "./components/TemplateFileSection/TemplateFileSection";
import NoteSection from "./components/NoteSection/NoteSection";
import CustomFieldsSection from "./components/CustomFieldsSection/CustomFieldsSection";
import LogoUploader from "../PdfTemplateWidget/components/LogoUploader/LogoUploader";

interface DocxTemplateWidgetProps {
  onTemplateChange: () => void;
}

export default function DocxTemplateWidget({ onTemplateChange }: Readonly<DocxTemplateWidgetProps>) {
  const { template, setTemplate, forceRegenerate } = useContext(TemplateContext);
  const { token } = useContext(AuthContext);
  const { pushAlert } = useContext(AppContext);
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const [templateName, setTemplateName] = useState<string>("");
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  // Extract template name from DocxPath
  useEffect(() => {
    if (template?.DocxPath) {
      // Split by "/" to get the filename
      const pathParts = template.DocxPath.split("/");
      const fileName = pathParts[pathParts.length - 1];

      const lastUnderscoreIndex = fileName.lastIndexOf("_");

      if (lastUnderscoreIndex !== -1) {
        // Extract everything up to the last underscore
        const name = fileName.substring(0, lastUnderscoreIndex);
        setTemplateName(name);
      } else {
        // Fallback if no underscore found
        setTemplateName(fileName);
      }
    }
  }, [template?.DocxPath]);

  // Load custom fields from template
  useEffect(() => {
    if (template?.CustomDocxFields && Array.isArray(template.CustomDocxFields)) {
      // Convert to the correct format using name as key
      const fields: CustomField[] = template.CustomDocxFields.map((field) => ({
        Name: field.Name,
        Value: field.Value,
      }));
      setCustomFields(fields);
    } else {
      setCustomFields([]);
    }
  }, [template?.CustomDocxFields]);

  // Default to PDF output format (used for rendering), but don't show selection
  useEffect(() => {
    if (template && (!template.FileType || template.FileType !== "pdf")) {
      // Set PDF as default output format for rendering
      const updatedTemplate: Template = {
        ...template,
        FileType: "pdf",
      };
      setTemplate(updatedTemplate);
    }
  }, [template, setTemplate]);

  const downloadDocxTemplate = useCallback(async () => {
    if (!template?.ID || !token) return;

    setIsDownloadingDocx(true);
    try {
      const response = await apiGetDocxTemplate(token, template.ID);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${templateName || template.TemplateName || "template"}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      pushAlert({
        title: "Success",
        text: "DOCX template downloaded successfully",
        type: "success",
        closeButton: true,
        time: 3000,
      });
    } catch (error) {
      console.error("Failed to download DOCX template:", error);
      pushAlert({
        title: "Error",
        text: "Failed to download DOCX template",
        type: "error",
        closeButton: true,
        time: 5000,
      });
    } finally {
      setIsDownloadingDocx(false);
    }
  }, [token, template, templateName, pushAlert]);

  const handleReplaceTemplate = useCallback(() => {
    if (!template?.ID || !token) return;

    // Create a hidden file input element
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    fileInput.style.display = "none";

    // Handle file selection
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setIsDownloadingDocx(true); // Reuse the loading state
        try {
          // Upload the DOCX file to the existing template
          const response = await apiUploadDocxTemplate(token, template.ID, file);
          console.log("Response:", response.data);
          setTemplate({ ...template, DocxPath: response.data.filePath });

          // Update the template name based on the new file
          const fileName = file.name;
          const lastDotIndex = fileName.lastIndexOf(".");
          const nameWithoutExt = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
          if (nameWithoutExt === templateName) {
            console.log("Same name, forcing regeneration");
            forceRegenerate();
          }
          setTemplateName(nameWithoutExt);

          pushAlert({
            title: "Success",
            text: "Template file replaced successfully",
            type: "success",
            closeButton: true,
            time: 3000,
          });

          // Notify parent about template change
          onTemplateChange();
        } catch (error) {
          console.error("Failed to replace template file:", error);
          pushAlert({
            title: "Error",
            text: "Failed to replace template file",
            type: "error",
            closeButton: true,
            time: 5000,
          });
        } finally {
          setIsDownloadingDocx(false);
        }
      }
    };

    // Trigger the file selection dialog
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }, [token, template, setTemplate, pushAlert, onTemplateChange, templateName, forceRegenerate]);

  const updateExecutiveSummary = useCallback(
    (executiveSummary: string) => {
      if (!template) return;

      const updatedTemplate: Template = {
        ...template,
        ExecutiveSummary: executiveSummary,
      };
      setTemplate(updatedTemplate);
      onTemplateChange();
    },
    [template, setTemplate, onTemplateChange],
  );

  // const updateFooterText = useCallback((footerText: string) => {
  //     if (!template) return;

  //     const updatedTemplate: Template = {
  //         ...template,
  //         FooterText: footerText
  //     };
  //     setTemplate(updatedTemplate);
  //     onTemplateChange();
  // }, [template, setTemplate, onTemplateChange]);

  const updateCustomFields = useCallback(
    (fields: CustomField[]) => {
      if (!template) return;

      const updatedTemplate: Template = {
        ...template,
        CustomDocxFields: fields,
      };
      setTemplate(updatedTemplate);
      onTemplateChange();
    },
    [template, setTemplate, onTemplateChange],
  );

  if (!(template?.DocxPath)) return null;

  return (
    <div className={styles.container}>
      <NoteSection docx />

      <TemplateFileSection
        templateName={templateName}
        onDownload={downloadDocxTemplate}
        isDownloading={isDownloadingDocx}
        onReplace={handleReplaceTemplate}
        templateId={template.ID}
      />

      <LogoUploader />

      <TextFieldSection
        title="Executive Summary"
        value={template.ExecutiveSummary || ""}
        onChange={updateExecutiveSummary}
        placeholder="Enter your executive summary here..."
        rows={6}
      />

      <CustomFieldsSection customFields={customFields || []} onChange={updateCustomFields} />
    </div>
  );
}
